<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4" style="background-color: rgba(0, 0, 0, 0.65); backdrop-filter: blur(2px);">
    <div class="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100 text-left animate-fadeIn">
      
      <!-- Header -->
      <div style="background-color: #1e293b;" class="px-6 py-4 flex items-center justify-between text-white">
        <i class="fa fa-exchange-alt text-2xl text-amber-400"></i>
        <button
          type="button"
          @click="close"
          class="text-gray-400 hover:text-white text-2xl font-bold p-1 leading-none transition-colors cursor-pointer"
        >
          &times;
        </button>
      </div>

      <!-- Formulario -->
      <div class="p-6">
        <form @submit.prevent="submitAdvance">

          <!-- Banner Informativo de Operación -->
          <div class="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start space-x-2">
            <i class="fa fa-info-circle text-amber-600 mt-0.5"></i>
            <div>
              <span class="font-bold block">¿Cómo funciona el avance?</span>
              El dinero en <strong>Pesos (COP)</strong> se resta del efectivo físico de la caja, y los <strong>Bolívares (Bs)</strong> se suman a la caja a través del punto de venta o pago móvil.
            </div>
          </div>

          <!-- Tasa de Cambio -->
          <div class="mb-4">
            <div class="relative">
              <input
                type="number"
                step="0.01"
                min="0.01"
                v-model.number="form.rate"
                readonly
                tabindex="-1"
                required
                class="w-full px-3 py-2.5 rounded-lg font-black text-base text-gray-700 bg-gray-100 cursor-not-allowed select-none"
                style="border: 1.5px solid #d1d5db; outline: none;"
                placeholder="0.00"
              />
              <span class="absolute right-3 top-2.5 text-xs font-bold text-gray-500">COP/Bs</span>
            </div>
          </div>

          <!-- Cantidad Pesos (COP) <-> Bolívares (Bs) con cálculo automático bidireccional -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            
            <!-- Cantidad en Pesos (COP) que salen -->
            <div>
              <label class="block text-xs font-bold uppercase text-red-700 mb-1 flex items-center justify-between">
                <span>Pesos a Entregar (COP)</span>
                <span class="text-[10px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">Sale de Caja</span>
              </label>
              <div class="relative">
                <input
                  type="number"
                  step="any"
                  min="0"
                  v-model.number="form.amount_cop"
                  @input="onCopChange"
                  required
                  class="w-full px-3 py-2.5 rounded-lg font-extrabold text-base text-gray-800 bg-white"
                  style="border: 1.5px solid #d1d5db; outline: none;"
                  placeholder="0"
                />
                <span class="absolute right-3 top-2.5 text-xs font-bold text-gray-400">COP</span>
              </div>
              <span class="text-[10px] text-gray-500 mt-1 block">Fórmula: Bs × Tasa</span>
            </div>

            <!-- Cantidad en Bolívares (Bs) que entran -->
            <div>
              <label class="block text-xs font-bold uppercase text-blue-700 mb-1 flex items-center justify-between">
                <span>Bolívares a Cobrar (Bs)</span>
                <span class="text-[10px] font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">Entra por Punto</span>
              </label>
              <div class="relative">
                <input
                  type="number"
                  step="any"
                  min="0"
                  v-model.number="form.amount_bs"
                  @input="onBsChange"
                  required
                  class="w-full px-3 py-2.5 rounded-lg font-extrabold text-base text-gray-800 bg-white"
                  style="border: 1.5px solid #d1d5db; outline: none;"
                  placeholder="0.00"
                />
                <span class="absolute right-3 top-2.5 text-xs font-bold text-gray-400">Bs.</span>
              </div>
              <span class="text-[10px] text-gray-500 mt-1 block">Fórmula: COP ÷ Tasa</span>
            </div>
          </div>

          <!-- Método de Ingreso en Bolívares -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label class="block text-xs font-bold uppercase text-gray-700 mb-1">Medio de Cobro en Bs</label>
              <select
                v-model="form.payment_method_incoming"
                required
                class="w-full px-3 py-2.5 rounded-lg text-xs md:text-sm font-semibold text-gray-800 bg-white"
                style="border: 1.5px solid #d1d5db; outline: none;"
              >
                <option value="Punto de Venta">Punto de Venta (Tarjeta)</option>
                <option value="Pago Móvil">Pago Móvil</option>
                <option value="Transferencia Bs">Transferencia Bancaria</option>
                <option value="Biopago">Biopago</option>
              </select>
            </div>

            <!-- Referencia de cobro -->
            <div>
              <label class="block text-xs font-bold uppercase text-gray-700 mb-1">Referencia / Lote / Comprobante</label>
              <input
                type="text"
                v-model="form.reference"
                placeholder="Ej. Ref 48201 / Lote 32"
                class="w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-white"
                style="border: 1.5px solid #d1d5db; outline: none;"
              />
            </div>
          </div>

          <!-- Nota / Cliente (Opcional) -->
          <div class="mb-5">
            <label class="block text-xs font-bold uppercase text-gray-700 mb-1">Nombre de Cliente o Nota (Opcional)</label>
            <input
              type="text"
              v-model="form.description"
              placeholder="Ej. Avance cliente Juan Pérez"
              class="w-full px-3 py-2 rounded-lg text-sm text-gray-800 bg-white"
              style="border: 1.5px solid #d1d5db; outline: none;"
            />
          </div>

          <!-- Resumen del Movimiento -->
          <div v-if="form.amount_cop > 0 && form.amount_bs > 0" class="mb-5 p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-800">
            <div class="font-bold text-slate-900 mb-1 flex items-center">
              <i class="fa fa-receipt text-slate-700 mr-1.5"></i> Resumen de Cuadre de Caja:
            </div>
            <div class="flex justify-between py-0.5">
              <span class="text-red-700 font-semibold">- Egreso en Gaveta (COP):</span>
              <span class="font-bold text-red-800">COP {{ Number(form.amount_cop).toLocaleString('es-CO') }}</span>
            </div>
            <div class="flex justify-between py-0.5">
              <span class="text-emerald-700 font-semibold">+ Ingreso en Sistema (Bs):</span>
              <span class="font-bold text-emerald-800">Bs. {{ Number(form.amount_bs).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
            </div>
          </div>

          <!-- Botones -->
          <div class="flex items-center justify-between pt-3 border-t border-gray-100">
            <button
              type="button"
              @click="close"
              class="px-4 py-2.5 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="loading || !form.amount_cop || !form.amount_bs"
              class="px-5 py-2.5 text-xs md:text-sm font-bold text-white rounded-lg transition-all shadow-md flex items-center space-x-1.5 cursor-pointer"
              :class="(!form.amount_cop || !form.amount_bs || loading) ? 'bg-gray-400 opacity-60 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700'"
            >
              <i v-if="loading" class="fa fa-spinner fa-spin mr-1"></i>
              <i v-else class="fa fa-check-circle mr-1"></i>
              <span>Procesar Avance</span>
            </button>
          </div>
        </form>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'CashAdvanceModal',

  props: {
    show: {
      type: Boolean,
      default: false
    },
    defaultRate: {
      type: [Number, String],
      default: 2.89
    },
    cashRegisterId: {
      type: [Number, String],
      default: null
    }
  },

  data() {
    return {
      loading: false,
      lastEdited: 'cop',
      form: this.$inertia.form({
        rate: parseFloat(this.defaultRate) || 0,
        amount_cop: '',
        amount_bs: '',
        payment_method_incoming: 'Punto de Venta',
        reference: '',
        description: '',
        cash_register_id: this.cashRegisterId
      })
    }
  },

  watch: {
    show(newVal) {
      if (newVal) {
        const r = parseFloat(this.defaultRate) || 0
        if (r > 0) {
          this.form.rate = r
        }
      }
    },
    defaultRate(newVal) {
      const r = parseFloat(newVal) || 0
      if (r > 0) {
        this.form.rate = r
      }
    },
    cashRegisterId(newVal) {
      this.form.cash_register_id = newVal
    }
  },

  methods: {
    close() {
      this.form.reset()
      const r = parseFloat(this.defaultRate) || 0
      if (r > 0) {
        this.form.rate = r
      }
      this.$emit('close')
    },

    onRateChange() {
      const rate = parseFloat(this.form.rate) || 0
      if (rate <= 0) return

      if (this.lastEdited === 'cop' && this.form.amount_cop > 0) {
        // Pesos a Bolívares: COP / Tasa = Bs
        this.form.amount_bs = parseFloat((this.form.amount_cop / rate).toFixed(2))
      } else if (this.lastEdited === 'bs' && this.form.amount_bs > 0) {
        // Bolívares a Pesos: Bs * Tasa = COP
        this.form.amount_cop = Math.round(this.form.amount_bs * rate)
      }
    },

    onCopChange() {
      this.lastEdited = 'cop'
      const cop = parseFloat(this.form.amount_cop) || 0
      const rate = parseFloat(this.form.rate) || 1

      if (cop > 0 && rate > 0) {
        // Pesos a Bolívares: Cantidad COP / Tasa = Total Bs
        this.form.amount_bs = parseFloat((cop / rate).toFixed(2))
      } else if (!cop) {
        this.form.amount_bs = ''
      }
    },

    onBsChange() {
      this.lastEdited = 'bs'
      const bs = parseFloat(this.form.amount_bs) || 0
      const rate = parseFloat(this.form.rate) || 1

      if (bs > 0 && rate > 0) {
        // Bolívares a Pesos: Cantidad Bs * Tasa = Total COP
        this.form.amount_cop = Math.round(bs * rate)
      } else if (!bs) {
        this.form.amount_cop = ''
      }
    },

    submitAdvance() {
      if (this.loading) return
      this.loading = true

      this.form.post('/cash-transactions/cash-advance', {
        preserveScroll: true,
        onSuccess: (page) => {
          this.loading = false
          const flash = page?.props?.flash || this.$page?.props?.flash || {}
          if (flash.error) {
            if (this.$swal) {
              this.$swal.fire({
                icon: 'error',
                title: 'No se pudo procesar el avance',
                text: flash.error,
                confirmButtonColor: '#dc2626'
              })
            } else {
              alert('No se pudo procesar el avance: ' + flash.error)
            }
          } else {
            const successMsg = flash.success || 'Avance de efectivo registrado exitosamente.'
            if (this.$swal) {
              this.$swal.fire({
                icon: 'success',
                title: '¡Avance Registrado!',
                text: successMsg,
                timer: 4000,
                showConfirmButton: true,
                confirmButtonColor: '#00b074'
              })
            } else {
              alert(successMsg)
            }
            this.close()
          }
        },
        onError: (errors) => {
          this.loading = false
          const msg = Object.values(errors).flat().join('\n') || 'Ocurrió un error al procesar el avance.'
          if (this.$swal) {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: msg,
              confirmButtonColor: '#dc2626'
            })
          } else {
            alert(msg)
          }
        }
      })
    }
  }
}
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fadeIn {
  animation: fadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
</style>
